import { objectToSelectOptions } from '@monetize/utils/core';
import { z } from 'zod';
export interface IQuickBook {
  companyId: string;
  refundLineItemId?: string;
  environment?: QuickBookEnvEnum;
  enabled: boolean;
}
export interface AccountingConnectionsResponse {
  quickbooks: IQuickBook | null;
  netsuite: INetsuite | null;
  xero: Xero | null;
  rillet: Rillet | null;
}

export enum QuickBookEnvEnum {
  SANDBOX = 'Sandbox',
  PRODUCTION = 'Production',
}
export const QuickBookEnvEnumZ = z.enum(QuickBookEnvEnum);

export const QuickBookSchema = z.object({
  environment: QuickBookEnvEnumZ,
});

export type IQuickBookSchema = z.infer<typeof QuickBookSchema>;

export enum NetsuiteCustomerLookupEnum {
  id = 'id',
  customId = 'customId',
  accountName = 'accountName',
  NONE = 'NONE', // This is only for form validation, never used on actual backend
}
export const NetsuiteCustomerLookupEnumZ = z.enum(NetsuiteCustomerLookupEnum);

export const NetsuiteCustomerLookupEnumDisplay: Record<
  NetsuiteCustomerLookupEnum,
  string
> = {
  NONE: 'None',
  accountName: 'Account Name',
  customId: 'Custom ID',
  id: 'MonetizeNow Account ID',
};
export const NETSUITE_CUSTOMER_LOOKUP_OPTIONS = objectToSelectOptions(
  NetsuiteCustomerLookupEnumDisplay,
);

export enum NetsuiteProductExtIdEnum {
  // sku = 'sku', // FIXME: this is broken on BE
  id = 'id',
}
export const NetsuiteProductExtIdEnumZ = z.enum(NetsuiteProductExtIdEnum);

export const NetsuiteProductExtIdEnumDisplay: Record<
  NetsuiteProductExtIdEnum,
  string
> = {
  id: 'MonetizeNow Product ID',
  // sku: 'SKU', // FIXME: this is broken on BE
};
export const NETSUITE_PRODUCT_EXT_ID_OPTIONS = objectToSelectOptions(
  NetsuiteProductExtIdEnumDisplay,
);

export const NetsuiteSchema = z.object({
  accountId: z.string(),
  companyId: z.string(),
  consumerKey: z.string(),
  consumerSecret: z.string(),
  tokenId: z.string(),
  tokenSecret: z.string(),
  customerLookup: NetsuiteCustomerLookupEnumZ.nullish(),
  netsuiteProductExternalId: NetsuiteProductExtIdEnumZ,
  taxItemId: z.string().nullish(),
  apiVersion: z.string().nullish(),
  enabled: z.boolean(),
});

export type INetsuite = z.infer<typeof NetsuiteSchema>;

export const QuickBookRefundSyncSchema = z.object({
  productId: z.string(),
});

export type IQuickBookRefundSyncSchema = z.infer<
  typeof QuickBookRefundSyncSchema
>;

interface QuickbooksRedirectPayload {
  type: 'quickbooks';
  env: QuickBookEnvEnum;
}
interface XeroRedirectPayload {
  type: 'xero';
}
export type AccountingRedirectPayload =
  | QuickbooksRedirectPayload
  | XeroRedirectPayload;

export const XeroUserInfoResponseSchema = z.object({
  username: z.string(),
  name: z.string(),
  email: z.string(),
  userId: z.string(),
});
export type XeroUserInfoResponse = z.infer<typeof XeroUserInfoResponseSchema>;

export const XeroCompanySchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type XeroCompany = z.infer<typeof XeroCompanySchema>;

export const XeroCompaniesListResponseSchema = z.object({
  companies: z.array(XeroCompanySchema),
});
export type XeroCompaniesListResponse = z.infer<
  typeof XeroCompaniesListResponseSchema
>;

export const XeroSetCompanyRequestSchema = z.object({
  companyId: z.string(),
});
export type XeroSetCompanyRequest = z.infer<typeof XeroSetCompanyRequestSchema>;

export const XeroCoaEntitySchema = z.object({
  code: z.string(),
  name: z.string(),
  type: z.string(),
});
export type XeroCoaEntity = z.infer<typeof XeroCoaEntitySchema>;

export const XeroCoaUpdateEntitySchema = XeroCoaEntitySchema.omit({
  type: true,
});
export type XeroCoaUpdateEntity = z.infer<typeof XeroCoaUpdateEntitySchema>;

export const XeroCoaUpdateRequestSchema = z.object({
  defaultProductCoa: XeroCoaUpdateEntitySchema.nullish(),
  defaultPaymentCoa: XeroCoaUpdateEntitySchema.nullish(),
});
export type XeroCoaUpdateRequest = z.infer<typeof XeroCoaUpdateRequestSchema>;

export const XeroCoaListResponseSchema = z.object({
  paymentCoas: z.array(XeroCoaEntitySchema),
  productCoas: z.array(XeroCoaEntitySchema),
});
export type XeroCoaListResponse = z.infer<typeof XeroCoaListResponseSchema>;

export const XeroSchema = z.object({
  companyId: z.object({
    value: z.string(),
  }),
  defaultProductCoa: XeroCoaEntitySchema,
  defaultPaymentCoa: XeroCoaEntitySchema,
  environment: z.string(),
  enabled: z.boolean(),
});
export type Xero = z.infer<typeof XeroSchema>;

export const XeroFormSchema = z.object({
  companyId: z.string().min(1, 'Company is required'),
  name: z.string().optional(),
  email: z.email(),
  userName: z.string().optional(),
  userId: z.string().optional(),
  defaultProductCoa: XeroCoaUpdateEntitySchema.nullish(),
  defaultPaymentCoa: XeroCoaUpdateEntitySchema.nullish(),
});
export type XeroFormType = z.infer<typeof XeroFormSchema>;

export enum RilletEnvEnum {
  SANDBOX = 'Sandbox',
  PRODUCTION = 'Production',
}
export const RilletEnvEnumZ = z.enum(RilletEnvEnum);

export const RilletCoaEntitySchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
});
export type RilletCoaEntity = z.infer<typeof RilletCoaEntitySchema>;

export const RilletCoaListResponseSchema = z.object({
  paymentCoas: z.array(RilletCoaEntitySchema),
});
export type RilletCoaListResponse = z.infer<typeof RilletCoaListResponseSchema>;

export const RilletSchema = z.object({
  apiKey: z.string(),
  environment: RilletEnvEnumZ,
  defaultPaymentAccountCode: z.string().nullish(),
  enabled: z.boolean(),
});
export type Rillet = z.infer<typeof RilletSchema>;

export const RilletFormSchema = RilletSchema.pick({
  apiKey: true,
  environment: true,
  defaultPaymentAccountCode: true,
}).extend({
  apiKey: z.string().min(1, 'API Key is required'),
});
export type RilletFormType = z.infer<typeof RilletFormSchema>;
